import axios from "axios";

/**
 * The backend URL is runtime-configurable. In the field the app should point
 * at the local Python bridge running on the tablet / laptop paired to the
 * TS12 (e.g. http://192.168.4.1:8765). Elsewhere it uses REACT_APP_BACKEND_URL
 * which resolves to the built-in cloud simulator.
 *
 * IMPORTANT: only the /api/station/* endpoints (real-hardware GeoCOM) route
 * through the configured bridge URL. Everything else — jobs, spool-surveys,
 * flanges, spool sessions — always hits the main backend (REACT_APP_BACKEND_URL)
 * because those endpoints don't exist on the standalone bridge process.
 */
const STORAGE_KEY = "gndc.backendUrl";
const DEFAULT_BACKEND = process.env.REACT_APP_BACKEND_URL;

export const getBackendUrl = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_BACKEND;
  } catch {
    return DEFAULT_BACKEND;
  }
};

export const setBackendUrl = (url) => {
  const clean = (url || "").trim().replace(/\/+$/, "");
  if (clean) localStorage.setItem(STORAGE_KEY, clean);
  else localStorage.removeItem(STORAGE_KEY);
  // Only the station axios clients follow the configured bridge URL. The
  // data-endpoint client keeps pointing at the main backend.
  stationApi.defaults.baseURL = `${getBackendUrl()}/api`;
  stationApiSlow.defaults.baseURL = `${getBackendUrl()}/api`;
};

export const isSimulatorMode = () => getBackendUrl() === DEFAULT_BACKEND;

export const API = `${DEFAULT_BACKEND}/api`;

// Data-endpoint client — always points at the main backend, never at the
// bridge. Used for jobs, spool-surveys, flanges, spool sessions.
export const api = axios.create({ baseURL: API, timeout: 10000 });

// Station-endpoint clients — follow the bridge URL if the user has one
// configured, otherwise fall back to the main backend simulator.
// `stationApi` is the snappy 10s client used for angle-only reads; the
// `Slow` variant carries a 45s budget because motoring/measure/atr on a
// real Bluetooth-SPP round-trip can easily exceed 10s.
export const stationApi = axios.create({
  baseURL: `${getBackendUrl()}/api`,
  timeout: 10000,
});
export const stationApiSlow = axios.create({
  baseURL: `${getBackendUrl()}/api`,
  timeout: 45000,
});

export const station = {
  state: () => stationApi.get("/station/state").then((r) => r.data),
  liveAngles: () => stationApi.get("/station/angles/live").then((r) => r.data),
  connect: () => stationApi.post("/station/connect").then((r) => r.data),
  disconnect: () => stationApi.post("/station/disconnect").then((r) => r.data),
  turn: (payload) => stationApiSlow.post("/station/turn", payload).then((r) => r.data),
  nudge: (direction, fast) =>
    stationApiSlow.post("/station/nudge", { direction, fast }).then((r) => r.data),
  measure: (mode = "coord") => stationApiSlow.post("/station/measure", { mode }).then((r) => r.data),
  /**
   * Convenience wrapper — take a normal measurement and return the
   * position of the shot in the instrument's **local frame** (dx/dy/dz
   * from the scope pivot). Derived from hz/v/sd, which is always
   * available and independent of whether the surveyor has set station
   * coordinates on the instrument.
   *
   * Local convention (matches Leica GeoCOM forward math):
   *   dx =  sd · sin(v) · sin(hz)     (Easting, m)
   *   dy =  sd · sin(v) · cos(hz)     (Northing, m)
   *   dz =  sd · cos(v)               (Vertical from scope pivot, m)
   *
   * Returns { hz, v, sd, x, y, z, timestamp } where x/y/z is now local
   * — safe to feed into any tool that needs to compute aim later. If
   * the instrument couldn't range (sd null) the promise rejects.
   */
  measureLocal: async (mode = "coord") => {
    const m = await stationApiSlow.post("/station/measure", { mode }).then((r) => r.data);
    if (!Number.isFinite(m?.hz) || !Number.isFinite(m?.v) || !Number.isFinite(m?.sd)) {
      throw new Error(m?.error || "measure failed — no distance");
    }
    const dx = m.sd * Math.sin(m.v) * Math.sin(m.hz);
    const dy = m.sd * Math.sin(m.v) * Math.cos(m.hz);
    const dz = m.sd * Math.cos(m.v);
    return { hz: m.hz, v: m.v, sd: m.sd, x: dx, y: dy, z: dz, timestamp: m.timestamp };
  },
  atr: (action) => stationApiSlow.post("/station/atr", { action }).then((r) => r.data),
  powerSearch: () => stationApiSlow.post("/station/atr", { action: "search" }).then((r) => r.data),
  reacquire: () => stationApiSlow.post("/station/atr", { action: "reacquire" }).then((r) => r.data),
  // AUT_FineAdjust (RPC 9037) — after the walk-in has driven the head
  // onto the prism, this snaps the crosshair EXACTLY onto its centre
  // (ATR fine-point, ~0.3″). Cuts residual angular error from the 1″
  // AUT_MakePositioning dead-zone to sub-arcsec so the final laser
  // landing is dead-on the mark. No-op on reflectorless targets.
  fineAdjust: () => stationApiSlow.post("/station/atr", { action: "fine-adjust" }).then((r) => r.data),
  setStartPoint: () => stationApiSlow.post("/station/atr", { action: "set-start-point" }).then((r) => r.data),
  clearStartPoint: () => stationApiSlow.post("/station/atr", { action: "clear-start-point" }).then((r) => r.data),
  returnToStart: () => stationApiSlow.post("/station/atr", { action: "return-to-start" }).then((r) => r.data),
  setTargetType: (target_type) =>
    stationApiSlow.post("/station/target-type", { target_type }).then((r) => r.data),
  // DynamicLock — enable prism-motion prediction so brief lock drops
  // (surveyor walks behind a beam) get extrapolated instead of a full
  // re-search. Returns {ok, acknowledged, on} — `acknowledged=false`
  // means the firmware doesn't expose the RPC and we're not toggling
  // anything on the instrument side.
  dynamicLock: (on) => stationApiSlow.post("/station/dynamic-lock", { on }).then((r) => r.data),
  // AutoHeight — read/write the stored tribrach height (metres).
  getInstrumentHeight: () => stationApi.get("/station/instrument-height").then((r) => r.data),
  setInstrumentHeight: (height_m) =>
    stationApiSlow.post("/station/instrument-height", { height_m }).then((r) => r.data),
  // Reflectorless single shot — no prism required. Surveyor aims the
  // telescope at a steel/concrete face, taps this, gets {hz, v, sd}
  // back. Bridge restores prism target mode afterwards.
  measureReflectorless: () => stationApiSlow.post("/station/measure-rl").then((r) => r.data),
  aim: (payload) => stationApi.post("/station/aim", payload).then((r) => r.data),
  homeSet: (payload) => stationApi.post("/station/home/set", payload).then((r) => r.data),
  homeGet: (tool, session_id) =>
    stationApi.get(`/station/home?tool=${tool}&session_id=${session_id}`).then((r) => r.data),
  homeReturn: (tool, session_id) =>
    stationApi.post("/station/home/return", { tool, session_id }).then((r) => r.data),
  homeClear: (tool, session_id) =>
    stationApi.delete(`/station/home?tool=${tool}&session_id=${session_id}`).then((r) => r.data),
  laser: (on) =>
    // Bridge-side set_laser now handles LOCK-release, reflectorless
    // flip and the settle-window internally, so this is a single
    // atomic HTTP call — no more races between two separate POSTs
    // and the bridge's poll loop.
    stationApiSlow.post("/station/laser", { on }).then((r) => r.data),
  slew: (vhz, vv) => stationApi.post("/station/slew", { vhz, vv }).then((r) => r.data),
  releaseMotors: () =>
    stationApi.post("/station/motors/release").then((r) => r.data),
  demo: (action, sizeDeg = 3, periodS = 6) =>
    stationApi.post("/station/demo", { action, size_deg: sizeDeg, period_s: periodS }).then((r) => r.data),
  demoText: (text, sizeDeg = 4, speedDegPerS = 6, loop = true) =>
    stationApi.post("/station/demo/text", { text, size_deg: sizeDeg, speed_deg_per_s: speedDegPerS, loop }).then((r) => r.data),
  setup: (payload) => stationApi.post("/station/setup", payload).then((r) => r.data),
  stakeout: (payload) =>
    stationApi.post("/station/stakeout", payload).then((r) => r.data),
  ping: (url) =>
    axios
      .get(`${url.replace(/\/+$/, "")}/api/station/state`, { timeout: 5000 })
      .then((r) => r.data),
};

export const jobs = {
  list: () => api.get("/jobs").then((r) => r.data),
  create: (payload) => api.post("/jobs", payload).then((r) => r.data),
  get: (id) => api.get(`/jobs/${id}`).then((r) => r.data),
  remove: (id) => api.delete(`/jobs/${id}`).then((r) => r.data),
  points: (id) => api.get(`/jobs/${id}/points`).then((r) => r.data),
  addPoint: (id, payload) =>
    api.post(`/jobs/${id}/points`, payload).then((r) => r.data),
  removePoint: (id) => api.delete(`/points/${id}`).then((r) => r.data),
  csvUrl: (id) => `${api.defaults.baseURL}/jobs/${id}/export.csv`,
};

/**
 * Free Station / Resection — Automet-style setup.
 * Back-shoot 2+ known control points → get TS station XYZ + Hz offset.
 */
export const resection = {
  solve: (sights) => api.post("/resection/solve", { sights }).then((r) => r.data),
};

/**
 * Structural inspection endpoints — as-built vs as-designed 3D checks.
 * All coordinates are in metres. `model_xyz` is in the DXF's own frame,
 * `world_xyz` is what the total station reports.
 */
export const structural = {
  listSessions: () => api.get("/structural/sessions").then((r) => r.data),
  create: (name) =>
    api.post("/structural/sessions", { name }).then((r) => r.data),
  get: (sid) => api.get(`/structural/sessions/${sid}`).then((r) => r.data),
  remove: (sid) => api.delete(`/structural/sessions/${sid}`).then((r) => r.data),
  upload: (sid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/structural/sessions/${sid}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      })
      .then((r) => r.data);
  },
  addAnchor: (sid, payload) =>
    api.post(`/structural/sessions/${sid}/anchors`, payload).then((r) => r.data),
  removeAnchor: (sid, aid) =>
    api.delete(`/structural/sessions/${sid}/anchors/${aid}`).then((r) => r.data),
  addCheck: (sid, payload) =>
    api.post(`/structural/sessions/${sid}/checks`, payload).then((r) => r.data),
  removeCheck: (sid, cid) =>
    api.delete(`/structural/sessions/${sid}/checks/${cid}`).then((r) => r.data),
  alignKabsch: (sid) =>
    api.post(`/structural/sessions/${sid}/align/kabsch`).then((r) => r.data),
  alignManual: (sid, payload) =>
    api.post(`/structural/sessions/${sid}/align/manual`, payload).then((r) => r.data),
  setTolerance: (sid, tolerance_mm) =>
    api.post(`/structural/sessions/${sid}/tolerance`, { tolerance_mm }).then((r) => r.data),
  csvUrl: (sid) => `${api.defaults.baseURL}/structural/sessions/${sid}/report.csv`,
};

/**
 * Target / offset device selector — global setting used by every feature.
 * Every measurement is corrected using the currently selected preset
 * (Reflectorless / GPR1 / Ball 25.4mm ⊥ 30mm / Custom …).
 */
export const target = {
  presets: () => api.get("/target/presets").then((r) => r.data),
  get: () => api.get("/target").then((r) => r.data),
  set: (payload) => api.put("/target", payload).then((r) => r.data),
};

/**
 * Verticality checker — plumb-line tracking along a column / mast / stack.
 * First shot defines the base (X0, Y0, Z0); every subsequent shot is
 * compared against a perfect vertical through the base.
 */
export const verticality = {
  listSessions: () => api.get("/verticality/sessions").then((r) => r.data),
  create: (name, tolerance_mm) =>
    api.post("/verticality/sessions", { name, tolerance_mm }).then((r) => r.data),
  get: (sid) => api.get(`/verticality/sessions/${sid}`).then((r) => r.data),
  remove: (sid) => api.delete(`/verticality/sessions/${sid}`).then((r) => r.data),
  addShot: (sid, payload) =>
    api.post(`/verticality/sessions/${sid}/shots`, payload).then((r) => r.data),
  removeShot: (sid, shot_id) =>
    api.delete(`/verticality/sessions/${sid}/shots/${shot_id}`).then((r) => r.data),
  setTolerance: (sid, tolerance_mm) =>
    api.post(`/verticality/sessions/${sid}/tolerance`, { tolerance_mm }).then((r) => r.data),
  csvUrl: (sid) => `${api.defaults.baseURL}/verticality/sessions/${sid}/report.csv`,
};

/**
 * Level checker — running Z-delta along a horizontal beam. Optional
 * design slope in mm/m. Beam direction is fixed by user-tagged START
 * and END anchor shots.
 */
export const level = {
  listSessions: () => api.get("/level/sessions").then((r) => r.data),
  create: (payload) =>
    api.post("/level/sessions", payload).then((r) => r.data),
  get: (sid) => api.get(`/level/sessions/${sid}`).then((r) => r.data),
  remove: (sid) => api.delete(`/level/sessions/${sid}`).then((r) => r.data),
  addShot: (sid, payload) =>
    api.post(`/level/sessions/${sid}/shots`, payload).then((r) => r.data),
  removeShot: (sid, shot_id) =>
    api.delete(`/level/sessions/${sid}/shots/${shot_id}`).then((r) => r.data),
  setTolerance: (sid, tolerance_mm) =>
    api.post(`/level/sessions/${sid}/tolerance`, { tolerance_mm }).then((r) => r.data),
  setSlope: (sid, design_slope_mm_per_m, slope_display_unit) =>
    api.post(`/level/sessions/${sid}/slope`, { design_slope_mm_per_m, slope_display_unit }).then((r) => r.data),
  setAnchors: (sid, payload) =>
    api.post(`/level/sessions/${sid}/anchors`, payload).then((r) => r.data),
  csvUrl: (sid) => `${api.defaults.baseURL}/level/sessions/${sid}/report.csv`,
};

/**
 * Ovality checker — best-fit circle on a cross-section (chimney, tank,
 * pipe, silo). Fitted via Kasa 2D LSQ; per-point deviation and diametral
 * ovality returned live as shots come in.
 */
export const ovality = {
  listSessions: () => api.get("/ovality/sessions").then((r) => r.data),
  create: (payload) =>
    api.post("/ovality/sessions", payload).then((r) => r.data),
  get: (sid) => api.get(`/ovality/sessions/${sid}`).then((r) => r.data),
  remove: (sid) => api.delete(`/ovality/sessions/${sid}`).then((r) => r.data),
  addShot: (sid, payload) =>
    api.post(`/ovality/sessions/${sid}/shots`, payload).then((r) => r.data),
  removeShot: (sid, shot_id) =>
    api.delete(`/ovality/sessions/${sid}/shots/${shot_id}`).then((r) => r.data),
  addReferenceShot: (sid, payload) =>
    api.post(`/ovality/sessions/${sid}/reference-shots`, payload).then((r) => r.data),
  removeReferenceShot: (sid, shot_id) =>
    api.delete(`/ovality/sessions/${sid}/reference-shots/${shot_id}`).then((r) => r.data),
  setTolerance: (sid, tolerance_mm) =>
    api.post(`/ovality/sessions/${sid}/tolerance`, { tolerance_mm }).then((r) => r.data),
  setTargetDiameter: (sid, target_diameter_mm) =>
    api.post(`/ovality/sessions/${sid}/target-diameter`, { target_diameter_mm }).then((r) => r.data),
  csvUrl: (sid) => `${api.defaults.baseURL}/ovality/sessions/${sid}/report.csv`,
};

export const dome = {
  listSessions: () => api.get("/dome/sessions").then((r) => r.data),
  create: (payload) => api.post("/dome/sessions", payload).then((r) => r.data),
  get: (sid) => api.get(`/dome/sessions/${sid}`).then((r) => r.data),
  patch: (sid, payload) => api.patch(`/dome/sessions/${sid}`, payload).then((r) => r.data),
  remove: (sid) => api.delete(`/dome/sessions/${sid}`).then((r) => r.data),
  addShot: (sid, payload) => api.post(`/dome/sessions/${sid}/shots`, payload).then((r) => r.data),
  removeShot: (sid, shot_id) => api.delete(`/dome/sessions/${sid}/shots/${shot_id}`).then((r) => r.data),
  addCutout: (sid, payload) => api.post(`/dome/sessions/${sid}/cutouts`, payload).then((r) => r.data),
  patchCutout: (sid, cid, payload) => api.patch(`/dome/sessions/${sid}/cutouts/${cid}`, payload).then((r) => r.data),
  removeCutout: (sid, cid) => api.delete(`/dome/sessions/${sid}/cutouts/${cid}`).then((r) => r.data),
  setoutGeometry: (sid, cid) => api.get(`/dome/sessions/${sid}/cutouts/${cid}/setout`).then((r) => r.data),
  outlineGeometry: (sid, cid, n = 64) => api.get(`/dome/sessions/${sid}/cutouts/${cid}/outline?n=${n}`).then((r) => r.data),
};

/**
 * Live presence — office/supervisor accounts can watch active sessions
 * update in real time. Field clients POST a lightweight heartbeat every
 * 5 s while a tool is open; observers poll `list` every 2 s and then
 * read the underlying session via the existing get() endpoints above.
 */
export const live = {
  heartbeat: (payload) => api.post("/live/presence", payload).then((r) => r.data),
  close: (tool, session_id) =>
    api.post("/live/presence/close", { tool, session_id }).then((r) => r.data),
  list: () => api.get("/live/presence").then((r) => r.data),
};

/** rad -> deg (0..360) */
export const radToDeg = (r) => (((r * 180) / Math.PI) % 360 + 360) % 360;
/** deg -> rad */
export const degToRad = (d) => (d * Math.PI) / 180;

/** Format Hz angle as DDD°MM'SS" */
export const formatDMS = (rad) => {
  const totalDeg = radToDeg(rad);
  const d = Math.floor(totalDeg);
  const mFloat = (totalDeg - d) * 60;
  const m = Math.floor(mFloat);
  const s = Math.floor((mFloat - m) * 60);
  return `${String(d).padStart(3, "0")}°${String(m).padStart(2, "0")}'${String(s).padStart(2, "0")}"`;
};

/** Format vertical angle (from zenith) in DMS */
export const formatVDMS = (rad) => formatDMS(rad);

export const fmt = (n, digits = 3) =>
  n === null || n === undefined || Number.isNaN(n)
    ? "—"
    : Number(n).toFixed(digits);
